import { useEffect, useRef, useState } from 'react';
import { palette } from '../../app/theme';

interface Props {
  html: string;
  /** 600 for desktop, 375 for a phone */
  width: number;
  /** clicking a block (rendered with data-block attributes) selects it */
  onSelect?: (blockId: string) => void;
}

/**
 * Shows a rendered email in a sandboxed iframe (no scripts run), sized to its content so the page scrolls, not the
 * frame. Links are inert: a click selects the block instead of navigating.
 */
export function EmailPreview({ html, width, onSelect }: Props) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(480);
  const select = useRef(onSelect);
  select.current = onSelect;

  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const onLoad = () => {
      const doc = el.contentDocument;
      if (!doc) return;
      const measure = () => setHeight(Math.max(240, doc.documentElement.scrollHeight));
      measure();
      // images change the height once they arrive
      doc.querySelectorAll('img').forEach((img) => img.addEventListener('load', measure));
      doc.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.closest('a')) e.preventDefault();
        const block = target.closest<HTMLElement>('[data-block]');
        if (block?.dataset.block) select.current?.(block.dataset.block);
      });
    };
    el.addEventListener('load', onLoad);
    return () => el.removeEventListener('load', onLoad);
  }, []);

  return (
    <div style={{ background: palette.canvas, border: `1px solid ${palette.border}`, borderRadius: 12, padding: '8px 0', overflow: 'hidden' }}>
      <iframe
        ref={frame}
        title="Email preview"
        sandbox="allow-same-origin"
        srcDoc={html}
        style={{ display: 'block', width: '100%', maxWidth: width === 600 ? '100%' : width, height, border: 0, margin: '0 auto', transition: 'max-width .2s' }}
      />
    </div>
  );
}
